import { reportCustomEvent } from '@/utils/arms';

import type { TPayPalV6CheckoutChannel } from '@/utils/arms/paypal-events';
import type { TStripeCheckoutChannel } from '@/utils/arms/stripe-events';

const ARMS_PERF_EVENT_TYPE = 'performance' as const;

// ─── 监控 1: 付费墙渲染耗时 ───────────────────────────────────────────────────

const PAYWALL_RENDER_EVENT_NAME = 'paywall_render_duration';
const PAYWALL_RENDER_EVENT_GROUP = 'reader_paywall';

export interface IPaywallRenderDurationProps {
    /** 书籍 ID */
    book_id: string;
    /** 章节 ID */
    chapter_id: string;
    /** 触发场景：'read_intercept'（顺序阅读被拦截）| 'catalog_click'（目录点击跳转） */
    trigger_scene: string;
    /** 余额是否充足：0 = 余额不足展示付费墙档位，1 = 余额充足展示解锁弹窗 */
    is_balance_enough: 0 | 1;
    /** 付费墙模板 ID（paywall_id_str），用于区分 A/B 实验组 */
    paywall_template_id: string;
    /** "Unlock Chapters" 标题展示 -> 档位列表渲染完成的毫秒数；预加载已就绪时为 0 */
    duration: number;
}

/**
 * 上报付费墙渲染耗时。
 * 起点：pay-wall.tsx "Unlock Chapters" 标题 div 渲染到 DOM。
 * 终点：RECHARGE_PAGE_SHOW 埋点触发（paywallLoading false + paywallId 存在）。
 * 预加载场景下终点早于起点，调用方应使用 Math.max(0, ...) clamp。
 */
export function reportPaywallRenderDuration(props: IPaywallRenderDurationProps): void {
    reportCustomEvent(PAYWALL_RENDER_EVENT_NAME, ARMS_PERF_EVENT_TYPE, {
        group: PAYWALL_RENDER_EVENT_GROUP,
        value: props.duration,
        properties: { ...props },
    });
}

// ─── 监控 2: PayPal 跳转链路耗时 ─────────────────────────────────────────────

const PAYPAL_REDIRECT_EVENT_NAME = 'paypal_redirect_duration';
const PAYPAL_REDIRECT_EVENT_GROUP = 'commerce';

export interface IPaypalRedirectDurationProps {
    /** 书籍 ID */
    book_id: string;
    /** 章节 ID（触发付费的锚点章节） */
    chapter_id: string;
    /** 用户选择的档位 product_id */
    gear_id: string;
    /** 商品类型：'subscribe'（订阅）| 'recharge'（充值） */
    goods_type: string;
    /** 当前页面路由（window.location.pathname），区分 /reader、/pay 等来源页面 */
    page_path: string;
    /** 创单成功后的订单 ID，用于跨系统串联排查 */
    order_id: string;
    /** preRedirectTime - clickTime：完整链路耗时（点击 -> 触发跳转），作为 ARMS value 上报 */
    total_time: number;
    /** apiStartTime - clickTime：点击到发出创单请求前的耗时（同步准备阶段，预期极小） */
    before_fetch_time: number;
    /** apiEndTime - apiStartTime：创单接口（POST /commerce/order/create/v1）网络耗时，主要瓶颈 */
    fetch_time: number;
    /** preRedirectTime - apiEndTime：接口返回到触发跳转前的耗时（persistPayReturnUrls + onGatewayShow，预期极小） */
    after_fetch_time: number;
    /** 出站渠道：v6_redirect / checkout_url / v6_fallback_* 等 */
    checkout_channel?: TPayPalV6CheckoutChannel;
}

/**
 * 上报点击档位到触发 PayPal 跳转的链路耗时。
 * 仅在成功路径（window.location.href 触发前）上报；失败已由 reportPayPalFail 覆盖。
 *
 * 时序：
 *   clickTime       startPayPalTopUp 入口（点击）
 *   apiStartTime    createCommerceOrder() 发出前
 *   apiEndTime      createCommerceOrder() 返回后
 *   preRedirectTime window.location.href 触发前
 */
export function reportPaypalRedirectDuration(props: IPaypalRedirectDurationProps): void {
    reportCustomEvent(PAYPAL_REDIRECT_EVENT_NAME, ARMS_PERF_EVENT_TYPE, {
        group: PAYPAL_REDIRECT_EVENT_GROUP,
        value: props.total_time,
        properties: { ...props },
    });
}

// ─── 监控 3: Stripe 跳转 / Sheet 链路耗时 ─────────────────────────────────────

const STRIPE_REDIRECT_EVENT_NAME = 'stripe_redirect_duration';
const STRIPE_SHEET_READY_EVENT_NAME = 'stripe_sheet_ready_duration';
const STRIPE_PERF_EVENT_GROUP = 'commerce';

export interface IStripeRedirectDurationProps {
    book_id: string;
    chapter_id: string;
    gear_id: string;
    goods_type: string;
    payment_method: string;
    page_path: string;
    order_id: string;
    total_time: number;
    before_fetch_time: number;
    fetch_time: number;
    after_fetch_time: number;
    checkout_channel?: TStripeCheckoutChannel;
}

/**
 * 上报点击档位到触发 Stripe Hosted Checkout 跳转的链路耗时。
 * 仅在成功路径（window.location.href 触发前）上报。
 */
export function reportStripeRedirectDuration(props: IStripeRedirectDurationProps): void {
    reportCustomEvent(STRIPE_REDIRECT_EVENT_NAME, ARMS_PERF_EVENT_TYPE, {
        group: STRIPE_PERF_EVENT_GROUP,
        value: props.total_time,
        properties: { ...props },
    });
}

export interface IStripeSheetReadyDurationProps {
    book_id: string;
    chapter_id: string;
    gear_id: string;
    goods_type: string;
    payment_method: string;
    order_id: string;
    /** Elements ready 时刻 - clickTime */
    duration: number;
    checkout_channel: TStripeCheckoutChannel;
}

/** 上报点击档位到 Payment Sheet Elements ready 的耗时 */
export function reportStripeSheetReadyDuration(props: IStripeSheetReadyDurationProps): void {
    reportCustomEvent(STRIPE_SHEET_READY_EVENT_NAME, ARMS_PERF_EVENT_TYPE, {
        group: STRIPE_PERF_EVENT_GROUP,
        value: props.duration,
        properties: { ...props },
    });
}
